import React from 'react'
import { Button, Grid, Row, Separator, Skeleton } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { type TreeId } from '@fs/zion-tree-types'
import { type GedcomStatus } from '../../../../services/gedcomTypes'

type Props = {
  treeId?: TreeId | null
  fileStatus?: GedcomStatus
  isProcessingOrLoading?: boolean
  isPrivateTree?: boolean
  isMobile?: boolean
  isManageTree?: boolean
  groupId?: string
  isResearcherTree?: boolean
  stacked?: boolean
}

export default function CetActions({
  treeId,
  fileStatus,
  isProcessingOrLoading,
  isPrivateTree,
  isMobile,
  isManageTree,
  groupId,
  isResearcherTree,
  stacked,
}: Props): JSX.Element | null {
  const [t] = useTranslation()

  if (!treeId) return null

  const viewButton = isManageTree ? (
    <Button
      key="view-tree-settings"
      noShrinkX
      data-testid="view-tree-settings-button"
      to={isResearcherTree ? `/research-tree/${groupId}/details` : `/family/${groupId}/details?from=manage-trees`}
      linkName="view-settings-button"
      disabled={fileStatus === 'IMPORTING'}
    >
      {t('tree-import.upload-trees.view-settings')}
    </Button>
  ) : (
    <Button
      key="view-tree"
      noShrinkX
      data-testid="gedcom-upload-view-personal-tree-button"
      to={`/tree/pedigree?treeId=${treeId}`}
      linkName="view-tree-button"
      disabled={fileStatus === 'IMPORTING'}
      external
    >
      {t('tree-import.upload-trees.view-tree')}
    </Button>
  )

  const reviewMatchesButton = isResearcherTree && (
    <Button
      key="review-matches"
      data-testid="review-matches-button"
      noShrinkX
      disabled={isPrivateTree || isProcessingOrLoading}
      disabledDescription={
        isPrivateTree && !isProcessingOrLoading ? t('tree-import.review-matches.disabled-hint') : undefined
      }
      to={`/matches/${treeId}`}
      linkName="review matches button"
    >
      {t('tree-import.review-matches.button')}
    </Button>
  )

  // The stacked design leads with settings; a row keeps the original order and leads with matches.
  // An array, not a fragment: `Row` and `Grid` give each child its own cell, and a fragment would
  // arrive as a single child, putting both buttons in one cell.
  const actionContent = stacked ? [viewButton, reviewMatchesButton] : [reviewMatchesButton, viewButton]

  if (stacked) {
    return <Grid guttersY="pico">{actionContent}</Grid>
  }

  return (
    <Row wrap guttersY="pico" guttersX="md" alignX={isMobile ? 'start' : 'end'} alignY="middle">
      {actionContent}
    </Row>
  )
}

export function CetActionsSkeleton({ isMobile, stacked }: { isMobile?: boolean; stacked?: boolean }): JSX.Element {
  // The stacked design shows settings above matches, so its skeleton is two buttons tall.
  if (stacked) {
    return (
      <>
        {/* eslint-disable-next-line test-selectors/button -- NA */}
        <Skeleton.Button minWidth={120} />
        <Separator size="nano" />
        {/* eslint-disable-next-line test-selectors/button -- NA */}
        <Skeleton.Button minWidth={150} />
      </>
    )
  }

  return (
    <Row wrap guttersY="nano" guttersX="md" alignX={isMobile ? 'start' : 'end'} alignY="middle">
      {/* eslint-disable-next-line test-selectors/button -- NA */}
      <Skeleton.Button width={isMobile ? 100 : 120} />
    </Row>
  )
}
